import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import { parseThemeApiResponse, provisioningAdminUrl } from "../ThemeManagement/themeApiUtils";

const STATUS_COLORS = {
  ACTIVE: "#059669",
  STAGING: "#2563eb",
  FAILED: "#dc2626",
  ROLLING_BACK: "#d97706",
  PROVISIONING: "#2563eb",
  SSL_INSTALLING: "#2563eb",
  DEPLOYING: "#2563eb",
  PENDING: "#6b7280",
  VERIFYING: "#6b7280",
};

const ProvisioningDashboard = () => {
  const token = decryptToken("accessToken") || "";
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);

  const loadSites = async () => {
    setLoading(true);
    try {
      const response = await fetch(provisioningAdminUrl("/"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        setSites(res.data || []);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (siteId) => {
    try {
      const response = await fetch(provisioningAdminUrl(`/${siteId}/logs`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        setLogs(res.data || []);
        setSelectedSite(siteId);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const postAction = async (siteId, action) => {
    try {
      const response = await fetch(provisioningAdminUrl(`/${siteId}/${action}`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        toast.success(res.message);
        loadSites();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  return (
    <>
      <div className="oms-header">
        <h1 className="dashboard-title">Site Provisioning</h1>
      </div>

      <p style={{ marginBottom: 16, color: "#666" }}>
        Monitor automated Plesk hosting, SSL, deployment, and theme activation for tenant websites.
      </p>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                <th style={{ padding: 10 }}>Domain</th>
                <th style={{ padding: 10 }}>Tenant</th>
                <th style={{ padding: 10 }}>Theme</th>
                <th style={{ padding: 10 }}>Status</th>
                <th style={{ padding: 10 }}>Step</th>
                <th style={{ padding: 10 }}>Updated</th>
                <th style={{ padding: 10 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: 10 }}>{site.domain}</td>
                  <td style={{ padding: 10 }}>
                    {site.admin_name || site.main_id}
                    <div style={{ fontSize: 12, color: "#888" }}>{site.main_id}</div>
                  </td>
                  <td style={{ padding: 10 }}>{site.theme_name || "—"}</td>
                  <td style={{ padding: 10, color: STATUS_COLORS[site.provision_status] || "#374151" }}>
                    {site.provision_status}
                  </td>
                  <td style={{ padding: 10 }}>{site.progress?.label || site.current_step || "—"}</td>
                  <td style={{ padding: 10 }}>
                    {site.updated_at ? new Date(site.updated_at).toLocaleString() : "—"}
                  </td>
                  <td style={{ padding: 10 }}>
                    <button type="button" style={{ marginRight: 6 }} onClick={() => loadLogs(site.id)}>
                      Logs
                    </button>
                    <button type="button" style={{ marginRight: 6 }} onClick={() => postAction(site.id, "retry")}>
                      Retry
                    </button>
                    {site.provision_status === "STAGING" && (
                      <button type="button" style={{ marginRight: 6 }} onClick={() => postAction(site.id, "promote")}>
                        Promote
                      </button>
                    )}
                    <button type="button" style={{ marginRight: 6 }} onClick={() => postAction(site.id, "force")}>
                      Force
                    </button>
                    <button type="button" onClick={() => postAction(site.id, "deactivate")}>
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedSite && (
        <div style={{ marginTop: 24 }}>
          <h3>Logs for site #{selectedSite}</h3>
          <div style={{ maxHeight: 320, overflow: "auto", background: "#fafafa", padding: 12, borderRadius: 8 }}>
            {logs.length === 0 ? (
              <p>No logs yet.</p>
            ) : (
              logs.map((log) => (
                <div key={log.id} style={{ marginBottom: 8, fontSize: 13 }}>
                  <strong>{log.step}</strong> · {log.status} ·{" "}
                  {new Date(log.created_at).toLocaleString()}
                  {log.message ? ` — ${log.message}` : ""}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProvisioningDashboard;
